import type { AlineFontName, AlineText, FontTypeFormat } from '../internal';

export function fontTypeAttribute(dataAttribute: string): string {
  return `font-type-${dataAttribute}-mark`;
}

export type FontTypeDetails = {
  dataAttribute: string;
  default: boolean;
  featured: boolean;
  fontFamily: string;
  match: RegExp;
  type: AlineFontName;
};

export const timesNewRomanDefault: FontTypeDetails = {
  dataAttribute: 'times-new-roman',
  default: true,
  featured: true,
  fontFamily: 'TimesNewRoman, "Times New Roman", Times, Baskerville, Georgia, serif',
  match: /times/i,
  type: 'Times New Roman',
};

export const lucidaSansTypewriterFamily =
  '"Lucida Sans Typewriter", "Lucida Console", monaco, "Bitstream Vera Sans Mono", monospace';
export const lucidaSansTypewriteType = 'Lucida Sans Typewriter';

// Pulling backup fonts from https://www.cssfontstack.com/
export const fontTypes: FontTypeDetails[] = [
  {
    dataAttribute: 'aptos-display',
    default: false,
    featured: true,
    fontFamily: '"Aptos Display", Arial, "Helvetica Neue", Helvetica, sans-serif',
    match: /aptos.*display/i,
    type: 'Aptos Display',
  },
  {
    dataAttribute: 'aptos-mono',
    default: false,
    featured: true,
    fontFamily: '"Aptos Mono", Arial, "Helvetica Neue", Helvetica, sans-serif',
    match: /aptos.*mono/i,
    type: 'Aptos Mono',
  },
  {
    dataAttribute: 'aptos-narrow',
    default: false,
    featured: true,
    fontFamily: '"Aptos Narrow", Arial, "Helvetica Neue", Helvetica, sans-serif',
    match: /aptos.*narrow/i,
    type: 'Aptos Narrow',
  },
  {
    dataAttribute: 'aptos-serif',
    default: false,
    featured: true,
    fontFamily: '"Aptos Serif", Arial, "Helvetica Neue", Helvetica, sans-serif',
    match: /aptos.*serif/i,
    type: 'Aptos Serif',
  },
  /**
   * This needs to go after the other Aptos fonts because it has a looser
   * RegExp match
   */
  {
    dataAttribute: 'aptos',
    default: false,
    featured: true,
    fontFamily: 'Aptos, Arial, "Helvetica Neue", Helvetica, sans-serif',
    match: /aptos/i,
    type: 'Aptos',
  },
  {
    dataAttribute: 'arial',
    default: false,
    featured: true,
    fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
    match: /arial/i,
    type: 'Arial',
  },
  {
    dataAttribute: 'cambria',
    default: false,
    featured: true,
    fontFamily: 'Cambria, Georgia, serif',
    match: /cambria/i,
    type: 'Cambria',
  },
  {
    dataAttribute: 'calibri',
    default: false,
    featured: false,
    fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif',
    match: /calibri/i,
    type: 'Calibri',
  },
  {
    dataAttribute: 'century-gothic',
    default: false,
    featured: false,
    fontFamily: '"Century Gothic", CenturyGothic, AppleGothic, sans-serif',
    match: /century.*gothic/i,
    type: 'Century Gothic',
  },
  {
    dataAttribute: 'courier-new',
    default: false,
    featured: false,
    fontFamily: '"Courier New", Courier, "Lucida Sans Typewriter", "Lucida Typewriter", monospace',
    match: /courier/i,
    type: 'Courier New',
  },
  {
    dataAttribute: 'franklin-gothic-medium',
    default: false,
    featured: false,
    fontFamily:
      '"Franklin Gothic Medium", "Franklin Gothic", "ITC Franklin Gothic", Arial, sans-serif',
    match: /franklin.*gothic/i,
    type: 'Franklin Gothic Medium',
  },
  {
    dataAttribute: 'garamond',
    default: false,
    featured: false,
    fontFamily:
      'Garamond, Baskerville, "Baskerville Old Face", "Hoefler Text", "Times New Roman", serif',
    match: /garamond/i,
    type: 'Garamond',
  },
  {
    dataAttribute: 'georgia',
    default: false,
    featured: false,
    fontFamily: 'Georgia, Times, "Times New Roman", serif',
    match: /georgia/i,
    type: 'Georgia',
  },
  /**
   * DO NOT REORDER: Helvetica Neue must come before Helvetica
   */
  {
    dataAttribute: 'helvetica-neue',
    default: false,
    featured: false,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    match: /helvetica.*neue/i,
    type: 'Helvetica Neue',
  },
  {
    dataAttribute: 'helvetica',
    default: false,
    featured: false,
    fontFamily: 'Helvetica, Arial, sans-serif',
    match: /helvetica/i,
    type: 'Helvetica',
  },
  {
    dataAttribute: 'lucida-sans-typewriter',
    default: false,
    featured: false,
    fontFamily: lucidaSansTypewriterFamily,
    match: /lucida.*typewriter/i,
    type: lucidaSansTypewriteType,
  },
  {
    dataAttribute: 'open-sans',
    default: false,
    featured: true,
    fontFamily: '"Open Sans", sans-serif',
    match: /open.*sans/i,
    type: 'Open Sans',
  },
  {
    dataAttribute: 'palatino',
    default: false,
    featured: false,
    fontFamily: 'Palatino, "Palatino Linotype", "Palatino LT STD", "Book Antiqua", Georgia, serif',
    match: /palatino/i,
    type: 'Palatino',
  },
  {
    dataAttribute: 'symbol',
    default: false,
    featured: false,
    fontFamily: 'Symbol, sans-serif',
    match: /symbol/i,
    type: 'Symbol',
  },
  {
    dataAttribute: 'tahoma',
    default: false,
    featured: false,
    fontFamily: 'Tahoma, Verdana, Segoe, sans-serif',
    match: /tahoma/i,
    type: 'Tahoma',
  },
  timesNewRomanDefault,
  {
    dataAttribute: 'verdana',
    default: false,
    featured: false,
    fontFamily: 'Verdana, Geneva, sans-serif',
    match: /verdana/i,
    type: 'Verdana',
  },
];

export function fontTypeToFontTypeFormat(
  fontType: FontTypeDetails,
  origin: 'USER_APPLIED' | 'INHERITED'
): FontTypeFormat {
  return {
    fontFamily: fontType.fontFamily,
    origin: origin,
    type: fontType.type,
  };
}

export function findDefaultFont(): FontTypeDetails {
  const fontType = fontTypes.find(t => t.default);
  if (!fontType) return timesNewRomanDefault;

  return fontType;
}

export function findFont(type: AlineText['fontType']['type']): {
  fontDetails: FontTypeDetails;
  found: boolean;
} {
  const fontType = fontTypes.find(t => type.match(t.match));
  return { fontDetails: fontType ?? timesNewRomanDefault, found: Boolean(fontType) };
}

const GENERIC_CSS_FONT_FAMILY_FALLBACKS: Record<string, AlineFontName> = {
  monospace: 'Courier New',
  'sans-serif': 'Arial',
  serif: 'Times New Roman',
};

export function resolveFontFromCssFamily(fontFamily: string): FontTypeDetails | undefined {
  const tokens = fontFamily.split(',').map(token => token.trim().replace(/^['"]|['"]$/g, ''));
  let genericFallback: FontTypeDetails | undefined;

  for (const token of tokens) {
    if (!token) continue;

    const normalized = token.toLowerCase();
    if (Object.keys(GENERIC_CSS_FONT_FAMILY_FALLBACKS).includes(normalized)) {
      const fallbackType = GENERIC_CSS_FONT_FAMILY_FALLBACKS[normalized];
      genericFallback ??= fontTypes.find(ft => ft.type === fallbackType);
      continue;
    }

    const matched = fontTypes.find(ft => ft.match.test(token));
    if (matched) return matched;
  }

  return genericFallback;
}
